import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import type { DocKind, DocumentSummary, TemplateSummary } from '@shared/api';
import { errorText } from '../api';
import { documents, templates as templateApi } from '../client';
import { useAppearance } from '../core/appearance';
import { Banner, Modal, useModalClose, useNotify } from '../core/ui';
import { Button, Chip, Segmented, Select, TextField } from '../core/controls';
import { Icon, rowsIcon, squaresFourIcon, uploadSimpleIcon } from '../core/icons';
import { prefetchEditor, whenIdle } from '../core/prefetch';
import { createRequest } from '../docs/templates/create';
import { predefinedSummaries } from '../docs/templates';
import { predefinedSlidesSummaries } from '../slides/templates/summaries';
import { DUR, motionOff } from '../motion';
import { useFlipList } from '../motion/flip';
import { useRouter } from '../router';
import { useApp } from '../state';
import { clearCache, readCache, readList, writeCache, writeList } from '../home/cache';
import { DocumentCard, DocumentRow, type RowActions } from '../home/Cards';
import { HomeHeader, type HomeContext } from '../home/HomeHeader';
import { optimistic } from '../home/optimistic';
import { SHELF_KEY as TEMPLATES_KEY } from '../home/shelf';
import { ImportStatus } from '../home/ImportStatus';
import { Rail } from '../home/Rail';
import { HomeSettings } from '../home/HomeSettings';
import { fromTemplate, StartSection, TemplateGrid } from '../home/StartSection';
import { useImportFiles } from '../home/useImportFiles';
import { cloudBridge } from '../desktop/cloud';
import { isDesktopUA } from '../desktop/detect';
import { LocalFilesSkeleton } from '../desktop/LocalFilesSkeleton';
import {
  docPath,
  emptyText,
  IMPORT_ACCEPT,
  KIND_CHIPS,
  OWNER_OPTIONS,
  ownedBy,
  RAIL,
  readLayout,
  VIEW_HEADING,
  writeLayout,
  type HomeView,
  type Layout,
  type OwnerFilter
} from '../home/home';
import '../home/home.css';

type Status = 'loading' | 'ready' | 'error';

// Inside the desktop app only: the files on this computer, and a file Upload to Office left for Home.
const HomeDesktop = lazy(() => import('../desktop/HomeDesktop'));

/** Whether the browser says it is online, for the footer. */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

function RenameBody({ title, setTitle, commit }: { title: string; setTitle: (t: string) => void; commit: (close: () => void) => void }) {
  const close = useModalClose();
  return (
    <TextField
      aria-label="Title"
      value={title}
      maxLength={200}
      autoFocus
      onChange={(e) => setTitle(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit(close);
      }}
    />
  );
}

function RenameFooter({ canSave, commit }: { canSave: boolean; commit: (close: () => void) => void }) {
  const close = useModalClose();
  return (
    <>
      <span style={{ flex: 1 }} />
      <Button variant="secondary" onClick={close}>
        Cancel
      </Button>
      <Button variant="suite" disabled={!canSave} onClick={() => commit(close)}>
        Save
      </Button>
    </>
  );
}

function RenameDialog({ doc, onSave, onClose }: { doc: DocumentSummary; onSave: (title: string) => void; onClose: () => void }) {
  const [title, setTitle] = useState(doc.title);
  // The dialog closes on the same press that saves, and the list has changed already.
  const commit = (close: () => void) => {
    const next = title.trim();
    if (!next) return;
    close();
    if (next !== doc.title) onSave(next);
  };
  return (
    <Modal title="Rename." width={440} onClose={onClose} footer={<RenameFooter canSave={title.trim() !== ''} commit={commit} />}>
      <RenameBody title={title} setTitle={setTitle} commit={commit} />
    </Modal>
  );
}

function DeleteFooter({ onConfirm }: { onConfirm: () => void }) {
  const close = useModalClose();
  return (
    <>
      <span style={{ flex: 1 }} />
      <Button variant="secondary" onClick={close}>
        Cancel
      </Button>
      <Button
        variant="danger"
        onClick={() => {
          close();
          onConfirm();
        }}
      >
        Delete forever
      </Button>
    </>
  );
}

/**
 * The home page: a shelf of ways to start, and then every document the reader can
 * open, by view. Drawn to the Main board, and answering in the same frame: the list
 * comes from the last visit while the server is asked, and every action changes the
 * screen first and the server second.
 */
export function Home() {
  const { me } = useApp();
  const { navigate } = useRouter();
  const notify = useNotify();
  const online = useOnline();
  const { appearance, setAppearance } = useAppearance();

  const desktop = isDesktopUA();
  const [location, setLocation] = useState<'all' | 'cloud' | 'local'>('all');
  const [view, setView] = useState<HomeView>('recent');
  const [kind, setKind] = useState<'' | DocKind>('');
  const [owner, setOwner] = useState<OwnerFilter>('any');
  const [layout, setLayoutState] = useState<Layout>(() => readLayout());

  // The list, from the last visit if there was one: painted now, refreshed below.
  const first = useMemo(() => readList('recent', ''), []);
  const [docs, setDocs] = useState<DocumentSummary[]>(first?.documents ?? []);
  const [next, setNext] = useState<string | null>(first?.nextCursor ?? null);
  const [status, setStatus] = useState<Status>(first ? 'ready' : 'loading');
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);

  const [shelf, setShelf] = useState<TemplateSummary[]>(() => readCache<TemplateSummary[]>(TEMPLATES_KEY) ?? []);
  const [renaming, setRenaming] = useState<DocumentSummary | null>(null);
  const [deleting, setDeleting] = useState<DocumentSummary | null>(null);
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(new Set());
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const picker = useRef<HTMLInputElement>(null);
  const dropped = useRef(new Map<string, { doc: DocumentSummary; index: number; timer: ReturnType<typeof setTimeout> }>());

  const setLayout = useCallback((l: Layout) => {
    setLayoutState(l);
    writeLayout(l);
  }, []);

  // The editor's code loads once Home has had its moment and is idle, so the first card of
  // the day opens fast without having raced Home's own first paint for the network.
  useEffect(() => whenIdle(() => prefetchEditor('docs'), 1200), []);

  // The shelf is a nicety: if it fails the blanks are still there.
  useEffect(() => {
    let live = true;
    templateApi
      .list()
      .then((r) => {
        if (!live) return;
        writeCache(TEMPLATES_KEY, r.templates);
        setShelf(r.templates);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  // The newest request wins: tab changes outrun the network.
  const seq = useRef(0);
  const load = useCallback(
    async (cursor?: string) => {
      if (view === 'templates') return;
      const mine = ++seq.current;
      if (cursor) setMore(true);
      try {
        const page = await documents.list({ view, ...(kind ? { kind } : {}), ...(cursor ? { cursor } : {}) });
        if (mine !== seq.current) return;
        setDocs((prev) => (cursor ? [...prev, ...page.documents] : page.documents));
        setNext(page.nextCursor);
        setStatus('ready');
        if (!cursor) writeList(view, kind, page);
      } catch (err) {
        if (mine !== seq.current) return;
        // A list that is already on screen stays: the error is about the refresh.
        setError(errorText(err, 'Could not load your documents.'));
        if (!cursor && !readList(view, kind)) setStatus('error');
      } finally {
        if (mine === seq.current) setMore(false);
      }
    },
    [view, kind]
  );

  useEffect(() => {
    if (view === 'templates') {
      setStatus('ready');
      return;
    }
    const cached = readList(view, kind);
    if (cached) {
      setDocs(cached.documents);
      setNext(cached.nextCursor);
      setStatus('ready');
    } else {
      setDocs([]);
      setNext(null);
      setStatus('loading');
    }
    void load();
  }, [view, kind, load]);

  // ── starting ──────────────────────────────────────────────────────────────

  const create = useCallback(
    async (k: DocKind, templateId?: string): Promise<void> => {
      prefetchEditor(k);
      try {
        const doc = await documents.create(await createRequest(k, templateId));
        clearCache();
        navigate(docPath(doc.kind, doc.id));
      } catch (err) {
        notify({ text: errorText(err, 'Could not make that document.'), tone: 'danger' });
      }
    },
    [navigate, notify]
  );

  // ── row actions: the screen first, the server second ──────────────────────

  const patch = (id: string, change: Partial<DocumentSummary>) =>
    setDocs((all) => all.map((d) => (d.id === id ? { ...d, ...change } : d)));

  /** A card leaves by fading and shrinking, and the ones after it slide up (FLIP). */
  const takeAway = (doc: DocumentSummary) => {
    const index = docs.findIndex((d) => d.id === doc.id);
    setLeaving((s) => new Set(s).add(doc.id));
    const timer = setTimeout(
      () => {
        setDocs((all) => all.filter((d) => d.id !== doc.id));
        setLeaving((s) => {
          const out = new Set(s);
          out.delete(doc.id);
          return out;
        });
      },
      motionOff() ? 0 : DUR.base
    );
    dropped.current.set(doc.id, { doc, index, timer });
  };

  const bringBack = (id: string) => {
    const held = dropped.current.get(id);
    if (!held) return;
    clearTimeout(held.timer);
    dropped.current.delete(id);
    setLeaving((s) => {
      const out = new Set(s);
      out.delete(id);
      return out;
    });
    setDocs((all) => (all.some((d) => d.id === id) ? all : [...all.slice(0, held.index), held.doc, ...all.slice(held.index)]));
  };

  const actions: RowActions = {
    onStar: (doc) => {
      const on = !doc.starred;
      void optimistic({
        apply: () => {
          patch(doc.id, { starred: on });
          if (!on && view === 'starred') takeAway(doc);
        },
        commit: () => documents.star(doc.id, on),
        rollback: () => {
          patch(doc.id, { starred: !on });
          bringBack(doc.id);
        },
        onError: (err) => notify({ text: errorText(err, 'Could not change the star.'), tone: 'danger' })
      }).then((ok) => ok && clearCache());
    },
    onRename: (doc) => setRenaming(doc),
    onCopy: async (doc) => {
      try {
        const copy = await documents.copy(doc.id);
        clearCache();
        notify({
          text: `Made “${copy.title}”.`,
          action: { label: 'Open', run: () => navigate(docPath(copy.kind, copy.id)) }
        });
        void load();
      } catch (err) {
        notify({ text: errorText(err, 'Could not make a copy.'), tone: 'danger' });
      }
    },
    onTrash: (doc) => {
      void optimistic({
        apply: () => takeAway(doc),
        commit: () => documents.trash(doc.id),
        rollback: () => bringBack(doc.id),
        onError: (err) => notify({ text: errorText(err, 'Could not move it to the trash.'), tone: 'danger' })
      }).then((ok) => {
        if (!ok) return;
        clearCache();
        notify({
          text: `Moved “${doc.title}” to the trash.`,
          action: {
            label: 'Undo',
            run: () =>
              void documents
                .restoreFromTrash(doc.id)
                .then(() => {
                  clearCache();
                  return load();
                })
                .catch((e: unknown) => notify({ text: errorText(e, 'Could not restore it.'), tone: 'danger' }))
          }
        });
      });
    },
    onRestore: (doc) => {
      void optimistic({
        apply: () => takeAway(doc),
        commit: () => documents.restoreFromTrash(doc.id),
        rollback: () => bringBack(doc.id),
        onError: (err) => notify({ text: errorText(err, 'Could not restore it.'), tone: 'danger' })
      }).then((ok) => {
        if (!ok) return;
        clearCache();
        notify({ text: `Restored “${doc.title}”.` });
      });
    },
    onDelete: (doc) => setDeleting(doc)
  };

  const rename = (doc: DocumentSummary, title: string) => {
    void optimistic({
      apply: () => patch(doc.id, { title }),
      commit: () => documents.rename(doc.id, title),
      rollback: () => patch(doc.id, { title: doc.title }),
      onError: (err) => notify({ text: errorText(err, 'Could not rename it.'), tone: 'danger' })
    }).then((ok) => ok && clearCache());
  };

  const deleteForever = (doc: DocumentSummary) => {
    void optimistic({
      apply: () => takeAway(doc),
      commit: () => documents.remove(doc.id),
      rollback: () => bringBack(doc.id),
      onError: (err) => notify({ text: errorText(err, 'Could not delete it.'), tone: 'danger' })
    }).then((ok) => ok && clearCache());
  };

  // ── files ─────────────────────────────────────────────────────────────────

  // Docs opens its files now; the others are recognised and the reader is told, rather than
  // being handed something half-working.
  const { takeFiles, warm, importing } = useImportFiles({ navigate, notify, reload: () => void load() });

  const hasFiles = (e: React.DragEvent) => [...(e.dataTransfer?.types ?? [])].includes('Files');
  const drag = {
    onDragEnter: (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
      warm();
    },
    onDragOver: (e: React.DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    },
    onDrop: (e: React.DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      takeFiles(e.dataTransfer.files);
    }
  };

  const headerCtx: HomeContext = useMemo(
    () => ({
      create: (k) => void create(k),
      pickFile: () => {
        // The picker takes a while to answer; the worker loads meanwhile.
        warm();
        picker.current?.click();
      },
      ...(desktop ? { openFile: () => void cloudBridge().then(c => c?.openFile()) } : {}),
      layout,
      setLayout,
      appearance,
      setAppearance
    }),
    [create, desktop, layout, setLayout, appearance, setAppearance, warm]
  );

  const [settingsOpen, setSettingsOpen] = useState(false);

  // ── drawing ───────────────────────────────────────────────────────────────

  const shown = useMemo(() => ownedBy(docs, owner, me?.id ?? null), [docs, owner, me]);
  const { container, item } = useFlipList(shown.map((d) => d.id));
  const isList = view !== 'templates';
  const orgName = me?.branding?.name ?? null;
  // Docs' own templates first, then Slides', then the reader's and the organisation's from the server.
  const everyTemplate = useMemo(() => [...predefinedSummaries(), ...predefinedSlidesSummaries(), ...shelf], [shelf]);

  return (
    <div {...drag} className="mv-home" data-offline={online ? undefined : 'true'}>
      <HomeHeader ctx={headerCtx} onSettings={() => setSettingsOpen(true)} />

      <div className="mv-home-body">
        <Rail view={view} onView={(v) => { setView(v); setLocation('all'); }} onSettings={() => setSettingsOpen(true)} computer={location === 'local'} onComputer={desktop ? () => { setView('recent'); setLocation('local'); } : undefined} />
        {settingsOpen && <HomeSettings ctx={headerCtx} onClose={() => setSettingsOpen(false)} />}

        <main className="mv-home-main">
          {/* The same choice, as chips, where there is no room for the rail. */}
          <div className="mv-home-views" role="group" aria-label="Sections">
            {RAIL.map((r) => (
              <Chip key={r.value} pressed={view === r.value} onClick={() => { setView(r.value); setLocation('all'); }}>
                {r.label}
              </Chip>
            ))}
          </div>

          {view === 'recent' && <div className="mv-home-hero">
            <div><h1 className="mv-home-h">Home.</h1><p>Pick up where you left off.</p></div>
            {desktop && <div className="mv-home-locations" role="group" aria-label="File location">
              {([['all', 'All files'], ['cloud', 'Cloud'], ['local', 'This computer']] as const).map(([value, label]) =>
                <Chip key={value} pressed={location === value} onClick={() => setLocation(value)}>{label}</Chip>
              )}
            </div>}
          </div>}
          {view === 'recent' && location !== 'local' && <StartSection templates={everyTemplate} orgName={orgName} onCreate={create} />}

          {(view !== 'recent' || location !== 'local') && <section aria-labelledby="mv-list-h" className="mv-home-panel mv-home-list">
            <div className="mv-home-list-head">
              <h2 id="mv-list-h" className="mv-home-section-h">
                {VIEW_HEADING[view]}
              </h2>
              {isList && (
                <div className="mv-home-chips" role="group" aria-label="Type">
                  {KIND_CHIPS.map((c) => (
                    <Chip key={c.value} pressed={kind === c.value} onClick={() => setKind(c.value)}>
                      {c.label}
                    </Chip>
                  ))}
                </div>
              )}
              <span style={{ flex: 1 }} />
              {isList && (
                <>
                  <Select
                    aria-label="Owner"
                    value={owner}
                    options={OWNER_OPTIONS}
                    onChange={(v) => setOwner(v as OwnerFilter)}
                    style={{ width: 168 }}
                  />
                  <Segmented
                    label="Layout"
                    value={layout}
                    onChange={setLayout}
                    items={[
                      { value: 'grid', label: 'Grid', icon: squaresFourIcon },
                      { value: 'list', label: 'List', icon: rowsIcon }
                    ]}
                  />
                </>
              )}
            </div>

            {view === 'templates' && <TemplateGrid entries={everyTemplate.map(fromTemplate)} onCreate={create} />}

            {isList && status === 'error' && (
              <Banner
                tone="danger"
                action={
                  <Button variant="secondary" size="sm" onClick={() => void load()}>
                    Try again
                  </Button>
                }
              >
                {error}
              </Banner>
            )}

            {isList && status === 'loading' && (
              <div className="mv-cgrid" aria-busy="true" aria-label="Loading">
                {Array.from({ length: 8 }, (_, i) => (
                  <div key={i} className="mv-skel-card" />
                ))}
              </div>
            )}

            {isList && status === 'ready' && shown.length === 0 && <p className="mv-home-empty">{emptyText(view)}</p>}

            {isList && status === 'ready' && shown.length > 0 && (
              <div
                ref={container as React.RefObject<HTMLDivElement>}
                className={layout === 'grid' ? 'mv-cgrid' : 'mv-clist'}
                role={layout === 'grid' ? 'list' : 'table'}
                aria-label="Documents"
              >
                {shown.map((d) =>
                  layout === 'grid' ? (
                    <DocumentCard key={d.id} doc={d} view={view} meId={me?.id ?? null} actions={actions} leaving={leaving.has(d.id)} item={item} />
                  ) : (
                    <DocumentRow key={d.id} doc={d} view={view} meId={me?.id ?? null} actions={actions} leaving={leaving.has(d.id)} item={item} />
                  )
                )}
              </div>
            )}

            {isList && status === 'ready' && next && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button variant="secondary" disabled={more} onClick={() => void load(next)}>
                  {more ? 'Loading' : 'Show more'}
                </Button>
              </div>
            )}
          </section>}
          {desktop && (
            <Suspense fallback={view === 'recent' && location !== 'cloud' ? <LocalFilesSkeleton /> : null}>
              <HomeDesktop takeFiles={takeFiles} showFiles={view === 'recent' && location !== 'cloud'} compact={location === 'all'} />
            </Suspense>
          )}
        </main>
      </div>

      <footer className="mv-home-foot" role="status">
        <span className="mv-dot" data-tone={online ? undefined : 'warn'} />
        {online ? 'Connected' : 'Offline'}
      </footer>

      <input
        ref={picker}
        type="file"
        accept={IMPORT_ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) takeFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <ImportStatus progress={importing} />

      {dragging && (
        <div aria-hidden="true" className="mv-home-drop">
          <Icon icon={uploadSimpleIcon} size={20} />
          Drop a file to open it
        </div>
      )}

      {renaming && <RenameDialog doc={renaming} onSave={(title) => rename(renaming, title)} onClose={() => setRenaming(null)} />}

      {deleting && (
        <Modal
          title={`Delete “${deleting.title}” forever?`}
          width={440}
          onClose={() => setDeleting(null)}
          footer={<DeleteFooter onConfirm={() => deleteForever(deleting)} />}
        >
          <p style={{ margin: 0, color: 'var(--fg-3)', fontSize: 15 }}>This cannot be undone.</p>
        </Modal>
      )}
    </div>
  );
}
